import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import type { Action, ThunkDispatch } from "@reduxjs/toolkit";
import {
  ActiveSelection,
  FabricImage,
  Group,
  PencilBrush,
  Textbox,
} from "fabric";
import {
  createStudioDesign,
  exportStudioDesign,
  fetchStudioBrandKit,
  fetchStudioDesign,
  fetchStudioFeatures,
  fetchStudioTemplates,
  importStudioStock,
  magicLayersStudioImage,
  removeStudioBackground,
  saveStudioBrandKit,
  saveStudioDesign,
  setStudioProfilePicture,
  upscaleStudioImage,
  uploadStudioAsset,
} from "../../features/studio-controller/StudioServices";
import type { Features, MediaItem } from "../../types/studioTypes";
import type { VideoStart } from "../../types/videoType";
import { fontsIn, loadFonts } from "./fonts";
import {
  AlignWhere,
  Canvas,
  EXTRA_PROPS,
  alignObjects,
  applyLook,
  applyTextEffect,
  attachSnapping,
  buildFromSpec,
  deserialize,
  distributeObjects,
  fitImageToFrame,
  linearGradient,
  loadImage,
  makeCombo,
  makeText,
  newId,
  panCrop,
  parseDoc,
  placeImage,
  renderPage,
  renderPageThumb,
  resizePageJson,
  snapshot,
  stringifyDoc,
  swapImageSource,
} from "./fabricKit";
import { Icon } from "./Icon";
import { NameField } from "./editor/Controls";
import { Panels, RAIL } from "./editor/Panels";
import { ContextToolbar } from "./editor/Toolbar";
import { PagesBar } from "./editor/PagesBar";
import {
  DownloadMenu,
  ResizeDialog,
  ShortcutsDialog,
  VersionsDrawer,
} from "./editor/dialogs";
import {
  AUTOSAVE_MS,
  DesignDetail,
  DesignEditorProps,
  FabricCanvas,
  SaveState,
  StudioObject,
  Timer,
  ToastKind,
  errMessage,
  iconBtnDark,
  iconBtnSm,
  menuBox,
  menuItem,
  railBtn,
  railOff,
  railOn,
  topBtn,
  topBtnDark,
} from "./Designeditorparts";

/* ------------------------------------------------------------ types */

type AppThunkDispatch = ThunkDispatch<unknown, unknown, Action>;
// Fabric objects carry many custom props (look, galleryId, assetUrl, effect, icon, qr, cropX...). Keep access permissive.
export type EObj = StudioObject & Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
type Json = any; // eslint-disable-line @typescript-eslint/no-explicit-any

function asFabricImage(
  obj: EObj | FabricImage | null | undefined,
): FabricImage | null {
  if (!obj) return null;

  if (obj instanceof FabricImage) {
    return obj;
  }

  if (obj.type === "image") {
    return obj as unknown as FabricImage;
  }

  return null;
}

function asEObj(obj: FabricImage | EObj): EObj {
  return obj as unknown as EObj;
}

interface PageDoc {
  id: string;
  json: Json;
}

interface BrandKit {
  colors: string[];
  logos: { galleryId: number | string }[];
  headingFont: string | null;
  bodyFont: string | null;
}
interface HistorySnap {
  pages: PageDoc[];
  index: number;
}
interface HistoryState {
  stack: HistorySnap[];
  index: number;
  restoring: boolean;
  timer: Timer | undefined;
}
interface CropState {
  img: FabricImage;
  drag: { p: { x: number; y: number }; start: { x: number; y: number } } | null;
  keep: { lockMovementX: boolean; lockMovementY: boolean };
}
interface StyleClip {
  group: StyleGroup;
  props: Record<string, unknown>;
  armed: boolean;
}
interface ResizeTarget {
  key: string;
  label: string;
  width: number;
  height: number;
}
interface DrawOpts {
  kind?: string;
  color: string;
  width: number;
}

type StyleGroup = "text" | "shape" | "image";
type Busy =
  | "image"
  | "gallery"
  | "profile"
  | "download"
  | "removebg"
  | "upscale"
  | "magic"
  | "resize"
  | null;
type Menu = "download" | "gallery" | null;
type Dialog = "resize" | "versions" | "shortcuts" | null;
type PanelKey = (typeof RAIL)[number][0] | string;
type DownloadFormat = "pdf" | "jpg" | "png" | "png-clear";

const HISTORY_LIMIT = 80;
const MAX_PAGES = 30;

const STYLE_PROPS: Record<StyleGroup, string[]> = {
  text: [
    "fontFamily",
    "fontSize",
    "fontWeight",
    "fontStyle",
    "underline",
    "linethrough",
    "fill",
    "textAlign",
    "charSpacing",
    "lineHeight",
    "effect",
    "shadow",
    "stroke",
    "strokeWidth",
    "paintFirst",
    "textBackgroundColor",
    "opacity",
  ],
  shape: [
    "fill",
    "stroke",
    "strokeWidth",
    "strokeDashArray",
    "opacity",
    "shadow",
    "rx",
    "ry",
  ],
  image: ["look", "opacity", "shadow"],
};

export const kindOf = (o: EObj | null | undefined): string | null => {
  if (!o) return null;
  if (o instanceof ActiveSelection || o.type === "activeselection")
    return "multi";
  if (o.placeholder) return "frame";
  if (o.qr) return "qr";
  if (o.icon) return "icon";
  const t = (o.type || "").toLowerCase();
  if (t === "textbox" || t === "i-text" || t === "text") return "text";
  if (t === "image") return "image";
  if (t === "group") return "group";
  if (t === "path" && !o.shapeKind) return "drawing";
  return "shape";
};
const styleGroup = (k: string | null): StyleGroup | null =>
  k === "text"
    ? "text"
    : k === "image"
      ? "image"
      : k === "shape" || k === "drawing"
        ? "shape"
        : null;

/**
 * The Canva-style design editor: multi-page designs, templates, photos (Gallery + free stock), AI background remover,
 * crop, filters, text effects, shapes, icons, QR codes, drawing, brand kit, smart guides, magic resize, version history
 * and export to PNG, JPG, PDF, the Gallery or the WhatsApp profile photo. Changes are saved automatically.
 */
export default function DesignEditor({
  designId: designIdProp,
  onClose: onCloseProp,
  onOpenDesign,
  onOpenVideo,
}: DesignEditorProps & {
  onOpenDesign?: (id: number | string) => void;
  /** Leaves the design (after saving) for a new video, from Elements → Videos / Audio. */
  onOpenVideo?: (start: VideoStart) => void;
}) {
  const dispatch = useDispatch<AppThunkDispatch>();
  // Opened inside Studio (props) or straight from /admin/design/:id (route)
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const designId = designIdProp ?? params.id;
  const onClose = onCloseProp ?? (() => navigate("/admin/studio"));
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = useRef<FabricCanvas | null>(null);
  const designRef = useRef<DesignDetail | null>(null);
  const pagesRef = useRef<PageDoc[]>([]);
  const pageIdxRef = useRef(0);
  const history = useRef<HistoryState>({
    stack: [],
    index: -1,
    restoring: false,
    timer: undefined,
  });
  const saveTimer = useRef<Timer | undefined>(undefined);
  const dirtyRef = useRef(false);
  const clipboardRef = useRef<EObj | null>(null);
  const clipPageRef = useRef(-1);
  const styleClipRef = useRef<StyleClip | null>(null);
  const cropRef = useRef<CropState | null>(null);
  const thumbJsonRef = useRef<Record<string, Json>>({});
  const coverThumbRef = useRef<{ json: Json; url: string | null }>({
    json: null,
    url: null,
  });
  const snapOnRef = useRef(true);
  const onKeyRef = useRef<(e: KeyboardEvent) => void>(() => {});

  const [design, setDesign] = useState<DesignDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [panel, setPanel] = useState<PanelKey>("templates");
  const [selected, setSelected] = useState<EObj | null>(null);
  const [, setTick] = useState(0);
  const [fit, setFit] = useState(1);
  const [zoomFactor, setZoomFactor] = useState(1);
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [toast, setToast] = useState<{ text: string; kind: ToastKind } | null>(
    null,
  );
  const [busy, setBusy] = useState<Busy>(null);
  const [menu, setMenu] = useState<Menu>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [pageIds, setPageIds] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [features, setFeatures] = useState<Features>({
    stockPhotos: false,
    removeBackground: false,
  });
  const [brand, setBrand] = useState<BrandKit>({
    colors: [],
    logos: [],
    headingFont: null,
    bodyFont: null,
  });
  const [crop, setCrop] = useState<{ id?: string } | null>(null);
  const [painting, setPainting] = useState(false);
  const [snapOn, setSnapOn] = useState(true);

  const refresh = () => setTick((t) => t + 1);
  const toastTimer = useRef<Timer | undefined>(undefined);
  const notify = (text: string, kind: ToastKind = "good") => {
    clearTimeout(toastTimer.current);
    setToast({ text, kind });
    toastTimer.current = setTimeout(
      () => setToast(null),
      kind === "bad" ? 7000 : 4200,
    );
  };
  const zoom = fit * zoomFactor;

  const objectsOf = (c: FabricCanvas) => c.getObjects() as EObj[];
  const activeOf = (c: FabricCanvas | null) =>
    (c?.getActiveObject() as EObj | undefined) ?? undefined;

  // ------------------------------------------------------------------ open

  useEffect(() => {
    let disposed = false;
    let detachSnap: (() => void) | null = null;
    (async () => {
      try {
        if (designId === undefined || designId === "") {
          throw new Error("No design to open.");
        }
        const [d, feats, kit] = (await Promise.all([
          dispatch(fetchStudioDesign(designId)).unwrap(),
          dispatch(fetchStudioFeatures())
            .unwrap()
            .catch(() => null),
          dispatch(fetchStudioBrandKit())
            .unwrap()
            .catch(() => null),
        ])) as unknown as [
          DesignDetail & { canvasJson?: string },
          Features | null,
          BrandKit | null,
        ];
        if (disposed) return;
        if (feats) setFeatures(feats);
        if (kit) setBrand(kit);
        const pages = parseDoc(d.canvasJson) as PageDoc[];
        await loadFonts([
          "Poppins",
          "Bebas Neue",
          "Dancing Script",
          "Noto Sans Devanagari",
          kit?.headingFont,
          kit?.bodyFont,
          ...pages.flatMap((p) => (p.json ? fontsIn(p.json) : [])),
        ]);
        if (disposed) return;
        designRef.current = d;
        setDesign(d);
        const c = new Canvas(canvasElRef.current as HTMLCanvasElement, {
          width: d.width,
          height: d.height,
          preserveObjectStacking: true,
          backgroundColor: "#ffffff",
          selectionColor: "rgba(11,122,90,0.08)",
          selectionBorderColor: "#0b7a5a",
          selectionLineWidth: 1.5,
          stopContextMenu: true,
          fireRightClick: true,
        });
        canvasRef.current = c;
        history.current.restoring = true;
        if (d.canvasJson) {
          await deserialize(c, pages[0].json);
        } else if (d.templateId) {
          const all = (await dispatch(fetchStudioTemplates()).unwrap()) as
            | { id: number | string; specJson: string }[]
            | null;
          const t = (all || []).find((x) => x.id === d.templateId);
          if (t) {
            const spec = JSON.parse(t.specJson);
            await loadFonts(
              (spec.layers || []).map(
                (l: { fontFamily?: string }) => l.fontFamily,
              ),
            );
            buildFromSpec(c, spec, d.width, d.height);
          }
          pages[0].json = snapshot(c);
        }
        history.current.restoring = false;
        if (disposed) return;
        pagesRef.current = pages;
        pageIdxRef.current = 0;
        setPageIds(pages.map((p) => p.id));
        wire(c);
        detachSnap = attachSnapping(
          c,
          () => {
            const d = designRef.current;

            if (!d) {
              return {
                width: c.getWidth(),
                height: c.getHeight(),
              };
            }

            return {
              width: d.width,
              height: d.height,
            };
          },
          () => snapOnRef.current,
        );
        pushHistory(true);
        if (!d.canvasJson) scheduleSave();
        refreshThumbs();
      } catch (e) {
        if (!disposed) setLoadError(errMessage(e));
      }
    })();
    return () => {
      disposed = true;
      clearTimeout(saveTimer.current);
      detachSnap?.();
      canvasRef.current?.dispose();
      canvasRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designId]);

  function wire(c: FabricCanvas) {
    const onSel = () => {
      const o = activeOf(c) ?? null;

      setSelected(o);

      if (
        cropRef.current &&
        (o as unknown) !== (cropRef.current.img as unknown)
      ) {
        endCrop();
      }
    };

    c.on("selection:created", onSel);
    c.on("selection:updated", onSel);
    c.on("selection:cleared", onSel);

    c.on("object:modified", () => {
      changed();
      refresh();
    });

    c.on("text:changed", () => changed());
    c.on("object:added", () => changed());
    c.on("object:removed", () => changed());
    c.on("path:created", (e: Json) => {
      e.path.set({ id: newId(), name: "Drawing" });
      changed();
    });
    c.on("mouse:dblclick", (e: Json) => {
      const t = e.target as EObj | undefined;
      if (t?.placeholder) {
        setPanel("photos");
        notify("Pick a photo to fill the frame.", "info");
        return;
      }
      if (t && kindOf(t) === "image" && !t.locked) startCrop(t);
    });
    c.on("mouse:down", (e: Json) => {
      // paint format: apply the copied style to the clicked object
      if (styleClipRef.current?.armed && e.target) {
        pasteStyle(e.target);
        styleClipRef.current.armed = false;
        setPainting(false);
        return;
      }
      const cr = cropRef.current;
      if (cr && (e.target as unknown) === (cr.img as unknown)) {
        cr.drag = {
          p: e.scenePoint,
          start: { x: cr.img.cropX, y: cr.img.cropY },
        };
      }
    });
    c.on("mouse:move", (e: Json) => {
      const cr = cropRef.current;
      if (cr?.drag) {
        panCrop(
          cr.img,
          cr.drag.start,
          e.scenePoint.x - cr.drag.p.x,
          e.scenePoint.y - cr.drag.p.y,
        );
        c.requestRenderAll();
      }
    });
    c.on("mouse:up", () => {
      const cr = cropRef.current;
      if (cr?.drag) {
        cr.drag = null;
        changed();
      }
    });
    // while cropping, show the hidden part of the photo faintly
    c.on("after:render", ({ ctx }: Json) => {
      const cr = cropRef.current;
      if (!cr) return;
      const img = cr.img;
      const el = img.getElement();
      const nat = img.getOriginalSize();
      const m = c.viewportTransform;
      const t = img.calcTransformMatrix();
      ctx.save();
      ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
      ctx.transform(t[0], t[1], t[2], t[3], t[4], t[5]);
      ctx.globalAlpha = 0.35;
      const x = -img.width / 2 - img.cropX;
      const y = -img.height / 2 - img.cropY;
      ctx.drawImage(el, 0, 0, el.width, el.height, x, y, nat.width, nat.height);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2 / (c.getZoom() * img.scaleX);
      ctx.setLineDash([
        8 / (c.getZoom() * img.scaleX),
        6 / (c.getZoom() * img.scaleX),
      ]);
      ctx.strokeRect(x, y, nat.width, nat.height);
      ctx.restore();
    });
  }

  // ------------------------------------------------------------------ fit to screen

  useEffect(() => {
    if (!design || !stageRef.current) return undefined;
    const stage = stageRef.current;
    const measure = () => {
      const r = stage.getBoundingClientRect();
      const f = Math.min(
        (r.width - 64) / design.width,
        (r.height - 64) / design.height,
      );
      setFit(Math.max(0.05, f));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [design]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !design) return;
    c.setDimensions({
      width: Math.round(design.width * zoom),
      height: Math.round(design.height * zoom),
    });
    c.setZoom(zoom);
    c.requestRenderAll();
  }, [zoom, design]);

  // ctrl + wheel zooms like Canva
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoomFactor((z) =>
        Math.min(5, Math.max(0.2, z * (e.deltaY > 0 ? 1 / 1.1 : 1.1))),
      );
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [design]);

  // ------------------------------------------------------------------ pages

  function commitPage() {
    const c = canvasRef.current;
    const p = pagesRef.current[pageIdxRef.current];
    if (c && p) p.json = snapshot(c);
  }

  async function loadPage(i: number) {
    const c = canvasRef.current;
    if (!c) return;
    endCrop();
    history.current.restoring = true;
    try {
      c.discardActiveObject();
      await deserialize(c, pagesRef.current[i].json);
      if (!c.backgroundColor) c.backgroundColor = "#ffffff";
    } finally {
      history.current.restoring = false;
    }
    pageIdxRef.current = i;
    setPageIndex(i);
    setSelected(null);
    c.requestRenderAll();
    setTimeout(thumbOfCurrent, 60);
  }

  async function goToPage(i: number) {
    if (i === pageIdxRef.current || i < 0 || i >= pagesRef.current.length)
      return;
    commitPage();
    thumbOfCurrent();
    await loadPage(i);
  }

  async function addPage(
    json: Json = null,
    at: number = pageIdxRef.current + 1,
  ) {
    if (pagesRef.current.length >= MAX_PAGES) {
      notify(`A design can have up to ${MAX_PAGES} pages.`, "bad");
      return;
    }
    commitPage();
    thumbOfCurrent();
    pagesRef.current.splice(at, 0, { id: newId(), json });
    setPageIds(pagesRef.current.map((p) => p.id));
    await loadPage(at);
    structuralChange();
  }

  async function duplicatePage(i: number = pageIdxRef.current) {
    commitPage();
    await addPage(pagesRef.current[i].json, i + 1);
  }

  async function deletePage(i: number = pageIdxRef.current) {
    if (pagesRef.current.length <= 1) {
      notify("A design needs at least one page.", "bad");
      return;
    }
    commitPage();
    pagesRef.current.splice(i, 1);
    setPageIds(pagesRef.current.map((p) => p.id));
    const next = Math.min(i, pagesRef.current.length - 1);
    await loadPage(next);
    structuralChange();
    notify("Page deleted. Press Ctrl+Z to bring it back.", "info");
  }

  function movePage(i: number, dir: number) {
    const j = i + dir;
    if (j < 0 || j >= pagesRef.current.length) return;
    commitPage();
    const list = pagesRef.current;
    [list[i], list[j]] = [list[j], list[i]];
    setPageIds(list.map((p) => p.id));
    if (pageIdxRef.current === i) {
      pageIdxRef.current = j;
      setPageIndex(j);
    } else if (pageIdxRef.current === j) {
      pageIdxRef.current = i;
      setPageIndex(i);
    }
    structuralChange();
  }

  function structuralChange() {
    pushHistory(false, true);
    dirtyRef.current = true;
    setSaveState("dirty");
    scheduleSave();
  }

  // ------------------------------------------------------------------ page thumbnails

  function thumbOfCurrent() {
    const c = canvasRef.current;
    const d = designRef.current;
    const p = pagesRef.current[pageIdxRef.current];
    if (!c || !d || !p || activeOf(c)?.isEditing) return;
    const url = c.toDataURL({
      format: "jpeg",
      quality: 0.7,
      multiplier: 200 / Math.max(d.width, d.height) / c.getZoom(),
    });
    thumbJsonRef.current[p.id] = p.json;
    setThumbs((t) => ({ ...t, [p.id]: url }));
  }

  async function refreshThumbs() {
    const d = designRef.current;
    if (!d) return;
    for (const p of [...pagesRef.current]) {
      if (
        thumbJsonRef.current[p.id] === p.json &&
        thumbJsonRef.current[p.id] !== undefined
      )
        continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        const url = await renderPageThumb(p.json, d.width, d.height, 200);
        thumbJsonRef.current[p.id] = p.json;
        setThumbs((t) => ({ ...t, [p.id]: url }));
      } catch {
        /* a broken page keeps its old preview */
      }
    }
  }

  // ------------------------------------------------------------------ history and autosave

  function changed() {
    const h = history.current;
    if (h.restoring) return;
    clearTimeout(h.timer);
    h.timer = setTimeout(() => {
      pushHistory(false);
      thumbOfCurrent();
    }, 250);
    dirtyRef.current = true;
    setSaveState("dirty");
    scheduleSave();
  }

  function pushHistory(initial: boolean, force = false) {
    const c = canvasRef.current;
    if (!c) return;
    commitPage();
    const h = history.current;
    const snap: HistorySnap = {
      pages: pagesRef.current.map((p) => ({ ...p })),
      index: pageIdxRef.current,
    };
    const last = h.stack[h.index];
    if (
      !initial &&
      !force &&
      last &&
      last.pages.length === snap.pages.length &&
      last.pages.every(
        (p, i) => p.id === snap.pages[i].id && p.json === snap.pages[i].json,
      )
    )
      return;
    h.stack = h.stack.slice(0, h.index + 1);
    h.stack.push(snap);
    if (h.stack.length > HISTORY_LIMIT) h.stack.shift();
    h.index = h.stack.length - 1;
    setCanUndo(h.index > 0);
    setCanRedo(false);
  }

  async function restore(step: number) {
    const c = canvasRef.current;
    const h = history.current;
    const next = h.index + step;
    if (!c || next < 0 || next >= h.stack.length) return;
    const snap = h.stack[next];
    pagesRef.current = snap.pages.map((p) => ({ ...p }));
    setPageIds(pagesRef.current.map((p) => p.id));
    await loadPage(Math.min(snap.index, pagesRef.current.length - 1));
    h.index = next;
    setCanUndo(next > 0);
    setCanRedo(next < h.stack.length - 1);
    dirtyRef.current = true;
    setSaveState("dirty");
    scheduleSave();
    refreshThumbs();
  }

  function scheduleSave() {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      save();
    }, AUTOSAVE_MS);
  }

  async function coverThumbnail(): Promise<string | undefined> {
    const d = designRef.current;
    const c = canvasRef.current;
    const first = pagesRef.current[0];
    if (!d || !c) return undefined;
    if (pageIdxRef.current === 0) {
      if (activeOf(c)?.isEditing) return undefined;
      return c.toDataURL({
        format: "jpeg",
        quality: 0.8,
        multiplier: 360 / Math.max(d.width, d.height) / c.getZoom(),
      });
    }
    if (coverThumbRef.current.json === first.json) return undefined; // unchanged since the last save
    const url = await renderPageThumb(first.json, d.width, d.height, 360);
    coverThumbRef.current = { json: first.json, url };
    return url;
  }

  const save = useCallback(async (extra: Record<string, unknown> = {}) => {
    const c = canvasRef.current;
    const d = designRef.current;
    if (!c || !d) return null;
    clearTimeout(saveTimer.current);
    setSaveState("saving");
    dirtyRef.current = false;
    try {
      commitPage();
      const body = {
        canvasJson: stringifyDoc(pagesRef.current),
        thumbnailDataUrl: await coverThumbnail(),
        ...extra,
      };
      const updated = (await dispatch(
        saveStudioDesign({ id: d.id, body }),
      ).unwrap()) as DesignDetail;
      designRef.current = { ...d, ...updated, canvasJson: undefined };
      setDesign((prev) =>
        prev
          ? { ...prev, name: updated.name, galleryId: updated.galleryId }
          : prev,
      );
      setSaveState(dirtyRef.current ? "dirty" : "saved");
      return updated;
    } catch (e) {
      dirtyRef.current = true;
      setSaveState("error");
      notify(errMessage(e), "bad");
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function close() {
    if (dirtyRef.current) await save();
    onClose?.();
  }

  /** Reloads the whole design from the server (after restoring a version). */
  async function reloadFrom(d: DesignDetail & { canvasJson?: string }) {
    const pages = parseDoc(d.canvasJson) as PageDoc[];
    await loadFonts(pages.flatMap((p) => (p.json ? fontsIn(p.json) : [])));
    designRef.current = d;
    setDesign(d);
    pagesRef.current = pages;
    setPageIds(pages.map((p) => p.id));
    thumbJsonRef.current = {};
    await loadPage(0);
    history.current = {
      stack: [],
      index: -1,
      restoring: false,
      timer: undefined,
    };
    pushHistory(true);
    setCanUndo(false);
    setCanRedo(false);
    dirtyRef.current = false;
    setSaveState("saved");
    refreshThumbs();
  }

  // ------------------------------------------------------------------ keyboard

  onKeyRef.current = (e: KeyboardEvent) => {
    const c = canvasRef.current;
    if (!c) return;
    const target = e.target as HTMLElement;
    const tag = (target.tagName || "").toLowerCase();
    if (
      tag === "input" ||
      tag === "textarea" ||
      tag === "select" ||
      target.isContentEditable
    )
      return;
    const o = activeOf(c);
    if (o?.isEditing) return;
    const mod = e.ctrlKey || e.metaKey;
    const k = e.key.toLowerCase();
    if (mod && k === "z") {
      e.preventDefault();
      restore(e.shiftKey ? 1 : -1);
      return;
    }
    if (mod && k === "y") {
      e.preventDefault();
      restore(1);
      return;
    }
    if (mod && k === "s") {
      e.preventDefault();
      save();
      return;
    }
    if (mod && e.altKey && k === "c") {
      e.preventDefault();
      copyStyle();
      return;
    }
    if (mod && e.altKey && k === "v") {
      e.preventDefault();
      if (o) pasteStyle(o);
      return;
    }
    if (mod && k === "c") {
      if (o) {
        e.preventDefault();
        copy();
      }
      return;
    }
    if (mod && k === "x") {
      if (o) {
        e.preventDefault();
        copy().then(removeSelected);
      }
      return;
    }
    if (mod && k === "v") {
      if (clipboardRef.current) {
        e.preventDefault();
        paste();
      }
      return;
    }
    if (mod && k === "a") {
      e.preventDefault();
      selectAll();
      return;
    }
    if (mod && k === "g") {
      e.preventDefault();
      if (e.shiftKey) ungroup();
      else group();
      return;
    }
    if (mod && e.key === "Enter") {
      e.preventDefault();
      addPage();
      return;
    }
    if (e.key === "?" || (e.shiftKey && k === "/")) {
      setDialog("shortcuts");
      return;
    }
    if (e.key === "Escape") {
      if (cropRef.current) {
        endCrop();
        return;
      }
      if (styleClipRef.current?.armed) {
        styleClipRef.current.armed = false;
        setPainting(false);
        return;
      }
      c.discardActiveObject();
      c.requestRenderAll();
      setSelected(null);
      return;
    }
    if (
      !mod &&
      !o &&
      (e.key === "PageDown" || (e.key === "ArrowRight" && e.altKey))
    ) {
      goToPage(pageIdxRef.current + 1);
      return;
    }
    if (
      !mod &&
      !o &&
      (e.key === "PageUp" || (e.key === "ArrowLeft" && e.altKey))
    ) {
      goToPage(pageIdxRef.current - 1);
      return;
    }
    if (!o) {
      if (!mod && k === "t") {
        e.preventDefault();
        setPanel("text");
      }
      return;
    }
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      removeSelected();
    } else if (mod && k === "d") {
      e.preventDefault();
      duplicate();
    } else if (mod && e.key === "]") {
      e.preventDefault();
      order(e.altKey ? "front" : "forward");
    } else if (mod && e.key === "[") {
      e.preventDefault();
      order(e.altKey ? "back" : "backward");
    } else if (e.shiftKey && mod && k === "l") {
      e.preventDefault();
      toggleLock();
    } else if (e.key.startsWith("Arrow") && !o.locked) {
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const dx =
        e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
      const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
      o.set({ left: o.left + dx, top: o.top + dy });
      o.setCoords();
      c.requestRenderAll();
      changed();
    }
  };

  useEffect(() => {
    const listener = (e: KeyboardEvent) => onKeyRef.current(e);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  // ------------------------------------------------------------------ adding things

  function add(
    obj: FabricImage | EObj | FabricImage[] | EObj[] | null | undefined,
    select = true,
  ) {
    const c = canvasRef.current;
    if (!c || !obj) return;

    const list = (Array.isArray(obj) ? obj : [obj]) as any[]; // eslint-disable-line @typescript-eslint/no-explicit-any

    list.forEach((x) => {
      if (!x.id) x.id = newId();
      c.add(x);
    });

    if (select) {
      const target = (
        list.length > 1 ? new ActiveSelection(list, { canvas: c }) : list[0]
      ) as EObj;

      c.setActiveObject(target);
      setSelected(target);
    }

    c.requestRenderAll();
  }

  /** Adds a stored image, or fills the selected photo frame with it. */
  async function addImage(
    item: { id?: number | string; mediaUrl: string },
    extra: Record<string, unknown> = {},
  ) {
    const c = canvasRef.current;
    const d = designRef.current;

    if (!c || !d) return null;

    setBusy("image");

    try {
      const img = (await loadImage(item.mediaUrl, {
        galleryId: item.id,
        assetUrl: item.mediaUrl,
        ...extra,
      })) as unknown as FabricImage;

      const target = activeOf(c);

      if (
        target &&
        (target.placeholder || (target.frame && kindOf(target) === "image"))
      ) {
        if (!target.frame) {
          placeImage(img, d.width, d.height);

          img.set({
            name: "Photo",
            galleryId: item.id,
            assetUrl: item.mediaUrl,
          });

          add(img);
        } else {
          fitImageToFrame(img, target.frame);

          img.set({
            name: "Photo",
            galleryId: item.id,
            assetUrl: item.mediaUrl,
          });

          if (target.look) {
            applyLook(img, target.look);
          }

          const index = objectsOf(c).indexOf(target);

          c.remove(target);
          c.insertAt(index, img);

          c.setActiveObject(img);
          setSelected(asEObj(img));
          c.requestRenderAll();
        }
      } else {
        placeImage(img, d.width, d.height);

        img.set({
          galleryId: item.id,
          assetUrl: item.mediaUrl,
          name: "Photo",
        });

        add(img);
      }

      changed();
      refresh();

      return img;
    } catch (e) {
      notify(errMessage(e), "bad");
      return null;
    } finally {
      setBusy(null);
    }
  }

  /** Stock photos: imports a free Pexels photo into the Gallery, then places it like any other image. */
  async function useStockPhoto(photo: {
    id: number | string;
    photographer: string;
  }) {
    setBusy("image");
    try {
      const item = (await dispatch(
        importStudioStock(photo.id),
      ).unwrap()) as MediaItem & { id?: number | string };
      return await addImage(item, {
        credit: `Photo by ${photo.photographer} on Pexels`,
      });
    } catch (e) {
      notify(errMessage(e), "bad");
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function setBackgroundPhoto(
    item: { id?: number | string; mediaUrl: string },
  ) {
    const c = canvasRef.current;
    const d = designRef.current;

    if (!c || !d) return;

    setBusy("image");

    try {
      objectsOf(c)
        .filter((o) => o.isBackgroundImage)
        .forEach((o) => c.remove(o));

      const img = (await loadImage(item.mediaUrl, {
        galleryId: item.id,
      })) as unknown as FabricImage;

      fitImageToFrame(img, {
        x: 0,
        y: 0,
        w: d.width,
        h: d.height,
        radius: 0,
      });

      img.set({
        name: "Background photo",
        isBackgroundImage: true,
        frame: undefined,
      });

      lockObject(asEObj(img), true);

      c.insertAt(0, img);
      c.discardActiveObject();
      setSelected(null);
      c.requestRenderAll();
    } catch (e) {
      notify(errMessage(e), "bad");
    } finally {
      setBusy(null);
    }
  }

  /** Makes the selected photo fill the whole page behind everything. */
  function imageToBackground(img: EObj) {
    const c = canvasRef.current;
    const d = designRef.current;

    if (!c || !d) return;

    const image = asFabricImage(img);

    if (!image) {
      notify("The selected object is not an image.", "bad");
      return;
    }

    objectsOf(c)
      .filter((o) => o.isBackgroundImage && (o as unknown) !== (img as unknown))
      .forEach((o) => c.remove(o));

    fitImageToFrame(image, {
      x: 0,
      y: 0,
      w: d.width,
      h: d.height,
      radius: 0,
    });

    image.set({
      name: "Background photo",
      isBackgroundImage: true,
      frame: undefined,
    });

    lockObject(asEObj(image), true);

    c.sendObjectToBack(image);
    c.discardActiveObject();
    setSelected(null);
    c.requestRenderAll();

    changed();
  }

  function setBackground(fill: string | string[], angle = 135) {
    const c = canvasRef.current;
    const d = designRef.current;
    if (!c || !d) return;
    c.backgroundColor = (
      Array.isArray(fill)
        ? linearGradient(fill, angle, d.width, d.height)
        : fill
    ) as typeof c.backgroundColor;
    c.requestRenderAll();
    changed();
    refresh();
  }

  function removeBackgroundPhoto() {
    const c = canvasRef.current;
    if (!c) return;
    objectsOf(c)
      .filter((o) => o.isBackgroundImage)
      .forEach((o) => c.remove(o));
    c.requestRenderAll();
    refresh();
  }

  // ------------------------------------------------------------------ editing the selection

  function targets(): EObj[] {
    const o = activeOf(canvasRef.current);
    if (!o) return [];
    return kindOf(o) === "multi"
      ? ((o as unknown as ActiveSelection).getObjects() as EObj[])
      : [o];
  }

  function setProp(props: Record<string, unknown>) {
    const c = canvasRef.current;
    const o = activeOf(c);

    if (!c || !o) return;

    o.set(props);
    o.setCoords();

    c.requestRenderAll();

    changed();
    refresh();
  }

  /** Runs fn(object) for every selected object, then saves the change. */
  function each(fn: (t: EObj) => void, filter?: (t: EObj) => boolean) {
    const c = canvasRef.current;
    if (!c) return;
    targets()
      .filter((t) => !filter || filter(t))
      .forEach((t) => {
        fn(t);
        t.setCoords?.();
      });
    activeOf(c)?.setCoords();
    c.requestRenderAll();
    changed();
    refresh();
  }

  function removeSelected() {
    const c = canvasRef.current;
    if (!c) return;
    const list = (c.getActiveObjects() as EObj[]).filter((o) => !o.locked);
    if (!list.length) return;
    c.discardActiveObject();
    c.remove(...list);
    c.requestRenderAll();
    setSelected(null);
  }

  async function duplicate() {
    await copy();
    await paste(true);
  }

  async function copy() {
    const c = canvasRef.current;
    const o = activeOf(c);
    if (!o) return;
    clipboardRef.current = (await o.clone(EXTRA_PROPS)) as EObj;
    clipPageRef.current = pageIdxRef.current;
  }

  async function paste(offsetOnly = false) {
    const c = canvasRef.current;
    if (!c || !clipboardRef.current) return;
    const cloned = (await clipboardRef.current.clone(EXTRA_PROPS)) as EObj;
    c.discardActiveObject();
    const shift =
      offsetOnly || pageIdxRef.current === clipPageRef.current ? 24 : 0;
    cloned.set({
      left: cloned.left + shift,
      top: cloned.top + shift,
      evented: true,
    });
    const fix = (x: EObj) => {
      x.set({ id: newId(), isBackgroundImage: false });
      lockObject(x, false);
    };
    if (kindOf(cloned) === "multi") {
      cloned.canvas = c;
      cloned.forEachObject((x: EObj) => {
        fix(x);
        c.add(x);
      });
      cloned.setCoords();
    } else {
      fix(cloned);
      c.add(cloned);
    }
    clipboardRef.current.set({
      left: clipboardRef.current.left + shift,
      top: clipboardRef.current.top + shift,
    });
    c.setActiveObject(cloned);
    setSelected(cloned);
    c.requestRenderAll();
  }

  function selectAll() {
    const c = canvasRef.current;
    if (!c) return;
    const list = objectsOf(c).filter((o) => !o.locked && o.visible !== false);
    if (!list.length) return;
    c.discardActiveObject();
    const sel = (
      list.length === 1 ? list[0] : new ActiveSelection(list, { canvas: c })
    ) as EObj;
    c.setActiveObject(sel);
    setSelected(sel);
    c.requestRenderAll();
  }

  function group() {
    const c = canvasRef.current;
    const o = activeOf(c);
    if (!c || !o || kindOf(o) !== "multi") return;
    const list = (o as unknown as ActiveSelection).getObjects();
    c.discardActiveObject();
    history.current.restoring = true;
    c.remove(...list);
    const g = new Group(list, {
      id: newId(),
      name: "Group",
      subTargetCheck: false,
      interactive: false,
    } as Json) as unknown as EObj;
    c.add(g);
    history.current.restoring = false;
    c.setActiveObject(g);
    setSelected(g);
    c.requestRenderAll();
    changed();
  }

  function ungroup() {
    const c = canvasRef.current;
    const g = activeOf(c);
    if (!c || !g || kindOf(g) !== "group") return;
    history.current.restoring = true;
    const index = objectsOf(c).indexOf(g);
    const list = (g as unknown as Group).removeAll();
    c.remove(g);
    list.forEach((x, i) => {
      c.insertAt(index + i, x);
      x.setCoords();
    });
    history.current.restoring = false;
    const sel = new ActiveSelection(list, { canvas: c }) as unknown as EObj;
    c.setActiveObject(sel);
    setSelected(sel);
    c.requestRenderAll();
    changed();
  }

  function order(where: "front" | "back" | "forward" | "backward") {
    const c = canvasRef.current;
    const o = activeOf(c);
    if (!c || !o) return;
    if (where === "front") c.bringObjectToFront(o);
    if (where === "back") {
      c.sendObjectToBack(o);
      const bg = objectsOf(c).find((x) => x.isBackgroundImage);
      if (bg && bg !== o) c.sendObjectToBack(bg);
    }
    if (where === "forward") c.bringObjectForward(o);
    if (where === "backward") c.sendObjectBackwards(o);
    c.requestRenderAll();
    changed();
    refresh();
  }

  function moveLayer(obj: EObj, toIndex: number) {
    const c = canvasRef.current;
    if (!c) return;
    c.moveObjectTo(obj, toIndex);
    c.requestRenderAll();
    changed();
    refresh();
  }

  function lockObject(o: EObj, locked: boolean) {
    o.set({
      locked,
      lockMovementX: locked,
      lockMovementY: locked,
      lockScalingX: locked,
      lockScalingY: locked,
      lockRotation: locked,
      hasControls: !locked,
      editable: !locked,
    });
  }

  function toggleLock(o: EObj | undefined = activeOf(canvasRef.current)) {
    if (!o) return;
    lockObject(o, !o.locked);
    canvasRef.current?.requestRenderAll();
    changed();
    refresh();
  }

  function toggleVisible(o: EObj) {
    const c = canvasRef.current;
    if (!c) return;
    o.set({ visible: o.visible === false });
    if (o.visible === false && activeOf(c) === o) c.discardActiveObject();
    c.requestRenderAll();
    changed();
    refresh();
  }

  function align(where: AlignWhere) {
    const c = canvasRef.current;
    const d = designRef.current;
    const o = activeOf(c);
    if (!c || !d || !o) return;
    if (kindOf(o) === "multi") {
      const list = (o as unknown as ActiveSelection).getObjects();
      c.discardActiveObject();
      alignObjects(list, where, d.width, d.height);
      const sel = new ActiveSelection(list, { canvas: c }) as unknown as EObj;
      c.setActiveObject(sel);
      setSelected(sel);
    } else {
      alignObjects([o], where, d.width, d.height);
    }
    c.requestRenderAll();
    changed();
  }

  function distribute(axis: "h" | "v") {
    const c = canvasRef.current;
    const o = activeOf(c);
    if (!c || !o || kindOf(o) !== "multi") return;
    const list = (o as unknown as ActiveSelection).getObjects();
    c.discardActiveObject();
    distributeObjects(list, axis);
    const sel = new ActiveSelection(list, { canvas: c }) as unknown as EObj;
    c.setActiveObject(sel);
    setSelected(sel);
    c.requestRenderAll();
    changed();
  }

  function selectLayer(o: EObj) {
    const c = canvasRef.current;
    if (!c || o.visible === false) return;
    c.setActiveObject(o);
    c.requestRenderAll();
    setSelected(o);
  }

  // ------------------------------------------------------------------ copy style (paint format)

  function copyStyle(o: EObj | undefined = activeOf(canvasRef.current)) {
    const g = styleGroup(kindOf(o));
    if (!o || !g) {
      notify("Select text, a shape or a photo to copy its style.", "info");
      return;
    }
    const props: Record<string, unknown> = {};
    STYLE_PROPS[g].forEach((p) => {
      const v = o[p];
      if (v !== undefined)
        props[p] =
          v && typeof v === "object" ? JSON.parse(JSON.stringify(v)) : v;
    });
    styleClipRef.current = { group: g, props, armed: true };
    setPainting(true);
    notify("Style copied. Click something to paste it (Esc to stop).", "info");
  }

  function pasteStyle(target: EObj) {
    const clip = styleClipRef.current;

    if (!clip) return;

    const list =
      kindOf(target) === "multi"
        ? ((target as unknown as ActiveSelection).getObjects() as EObj[])
        : [target];

    let n = 0;

    list.forEach((t) => {
      if (styleGroup(kindOf(t)) !== clip.group || t.locked) return;

      const { look, effect, ...rest } = clip.props as Record<string, Json>;

      t.set(rest);

      if (clip.group === "image" && look && t.type === "image") {
        applyLook(t as unknown as FabricImage, look);
      }
      if (clip.group === "text" && t.type === "textbox") {
        if (effect) {
          applyTextEffect(t as Textbox, { ...effect });
        } else {
          t.effect = null;
        }

        t.initDimensions?.();
      }

      t.setCoords();
      n += 1;
    });

    canvasRef.current?.requestRenderAll();

    if (n) {
      changed();
      refresh();
    } else {
      notify("That style only fits the same kind of element.", "info");
    }
  }

  // ------------------------------------------------------------------ crop mode

  function startCrop(img: EObj | undefined = activeOf(canvasRef.current)) {
    const c = canvasRef.current;

    if (!c || !img || kindOf(img) !== "image" || img.locked) {
      return;
    }

    const image = asFabricImage(img);

    if (!image) {
      return;
    }

    cropRef.current = {
      img: image,
      drag: null,
      keep: {
        lockMovementX: image.lockMovementX,
        lockMovementY: image.lockMovementY,
      },
    };

    image.set({
      lockMovementX: true,
      lockMovementY: true,
      hoverCursor: "grab",
      moveCursor: "grabbing",
    });

    c.setActiveObject(image);
    setSelected(asEObj(image));
    setCrop({ id: image.id });

    c.requestRenderAll();
  }

  function endCrop() {
    const cr = cropRef.current;
    if (!cr) return;
    cropRef.current = null;
    cr.img.set({ ...cr.keep, hoverCursor: null, moveCursor: null });
    setCrop(null);
    canvasRef.current?.requestRenderAll();
    changed();
  }

  // ------------------------------------------------------------------ AI

  /**
   * The picture's stored file. A picture that isn't stored (pasted, or from an old design) is
   * uploaded first, as it looks on the page without crop or filters.
   */
  async function storedSource(
    img: EObj,
  ): Promise<{ mediaUrl: string; galleryId?: number | string }> {
    if (img.assetUrl) {
      return { mediaUrl: img.assetUrl, galleryId: img.galleryId || undefined };
    }
    const el = (img as unknown as FabricImage).getElement?.() as
      | HTMLImageElement
      | HTMLCanvasElement
      | undefined;
    if (!el) throw new Error("This picture can't be read.");
    const c = document.createElement("canvas");
    c.width = (el as HTMLImageElement).naturalWidth || el.width;
    c.height = (el as HTMLImageElement).naturalHeight || el.height;
    c.getContext("2d")?.drawImage(el, 0, 0);
    const blob = await new Promise<Blob>((resolve, reject) =>
      c.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("This picture can't be read."))),
        "image/png",
      ),
    );
    const item = (await dispatch(
      uploadStudioAsset(new File([blob], "picture.png", { type: "image/png" })),
    ).unwrap()) as MediaItem;
    img.set({ assetUrl: item.mediaUrl, galleryId: item.id });
    return { mediaUrl: item.mediaUrl, galleryId: item.id };
  }

  async function removeImageBackground(img: EObj) {
    if (img?.type !== "image") return;
    setBusy("removebg");
    try {
      const src = await storedSource(img);
      const item = (await dispatch(removeStudioBackground(src)).unwrap()) as MediaItem & {
        id?: number | string;
      };
      await swapImageSource(img as unknown as FabricImage, item.mediaUrl);
      img.set({ galleryId: item.id, name: `${img.name || "Photo"} (cut-out)` });
      canvasRef.current?.requestRenderAll();
      changed();
      refresh();
      notify("Background removed. The cut-out is saved in your Gallery too.");
    } catch (e) {
      notify(errMessage(e), "bad");
    } finally {
      setBusy(null);
    }
  }

  /** Makes the picture's file 2× or 4× bigger; it keeps its size on the page and gets sharper. */
  async function upscaleImage(img: EObj, scale: 2 | 4 = 2) {
    if (img?.type !== "image") return;
    setBusy("upscale");
    try {
      const src = await storedSource(img);
      const item = (await dispatch(
        upscaleStudioImage({ ...src, scale }),
      ).unwrap()) as MediaItem;
      await swapImageSource(img as unknown as FabricImage, item.mediaUrl);
      img.set({ galleryId: item.id });
      canvasRef.current?.requestRenderAll();
      changed();
      refresh();
      notify(`Upscaled ${scale}×. The bigger picture is saved in your Gallery too.`);
    } catch (e) {
      notify(errMessage(e), "bad");
    } finally {
      setBusy(null);
    }
  }

  /**
   * Magic layers: the subject becomes its own layer on top, and the picture underneath gets the subject
   * filled in, so the subject can be moved or text put behind it.
   */
  async function magicLayers(img: EObj) {
    const c = canvasRef.current;
    if (!c || img?.type !== "image") return;
    setBusy("magic");
    try {
      const src = await storedSource(img);
      const { subject, background } = await dispatch(
        magicLayersStudioImage(src),
      ).unwrap();
      const subj = (await img.clone(EXTRA_PROPS)) as EObj;
      await swapImageSource(img as unknown as FabricImage, background.mediaUrl);
      img.set({ galleryId: background.id, name: `${img.name || "Photo"} (background)` });
      await swapImageSource(subj as unknown as FabricImage, subject.mediaUrl);
      subj.set({
        id: newId(),
        galleryId: subject.id,
        name: `${(img.name || "Photo").replace(/ \(background\)$/, "")} (subject)`,
        locked: false,
        isBackgroundImage: false,
        selectable: true,
        evented: true,
      });
      c.insertAt(objectsOf(c).indexOf(img) + 1, subj);
      c.setActiveObject(subj);
      setSelected(subj);
      c.requestRenderAll();
      changed();
      refresh();
      notify("Split into two layers: drag the subject, or add text and send it behind the subject.");
    } catch (e) {
      notify(errMessage(e), "bad");
    } finally {
      setBusy(null);
    }
  }

  // ------------------------------------------------------------------ drawing

  function setDrawing(opts: DrawOpts | null) {
    const c = canvasRef.current;
    if (!c) return;
    if (!opts) {
      c.isDrawingMode = false;
      return;
    }
    c.discardActiveObject();
    setSelected(null);
    const brush = new PencilBrush(c);
    const hex = opts.color;
    if (opts.kind === "highlighter") {
      const n = parseInt(hex.slice(1), 16);
      brush.color = `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},0.38)`;
      brush.strokeLineCap = "square";
    } else {
      brush.color = hex;
    }
    brush.width = opts.width;
    brush.decimate = 2;
    c.freeDrawingBrush = brush;
    c.isDrawingMode = true;
  }

  useEffect(() => {
    if (panel !== "draw") setDrawing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel]);

  // ------------------------------------------------------------------ export

  interface ExportOpts {
    format?: "png" | "jpeg";
    multiplier?: number;
    transparent?: boolean;
    quality?: number;
  }

  async function exportCurrent({
    format = "jpeg",
    multiplier = 1,
    transparent = false,
    quality = 0.92,
  }: ExportOpts = {}) {
    const c = canvasRef.current as FabricCanvas;
    endCrop();
    c.discardActiveObject();
    setSelected(null);
    const bg = c.backgroundColor;
    if (transparent) c.backgroundColor = "" as typeof c.backgroundColor;
    c.renderAll();
    const url = c.toDataURL({
      format: format as "jpeg" | "png",
      quality,
      multiplier: multiplier / c.getZoom(),
    });
    if (transparent) {
      c.backgroundColor = bg;
      c.renderAll();
    }
    return url;
  }

  async function pageImage(i: number, opts: ExportOpts = {}): Promise<string> {
    if (i === pageIdxRef.current) return exportCurrent(opts);
    const d = designRef.current as DesignDetail;
    return renderPage(pagesRef.current[i].json, d.width, d.height, {
      quality: 0.92,
      ...opts,
    });
  }

  const toBlob = async (url: string) => (await fetch(url)).blob();

  async function jpegUnder(i: number, limitMb = 4.8): Promise<Blob> {
    let blob = await toBlob(await pageImage(i, { format: "jpeg" }));
    if (blob.size > limitMb * 1024 * 1024)
      blob = await toBlob(
        await pageImage(i, { format: "jpeg", quality: 0.75 }),
      );
    return blob;
  }

  async function download({
    format,
    pages: which,
    scale,
  }: {
    format: DownloadFormat;
    pages: "all" | "current";
    scale: number;
  }) {
    setMenu(null);
    const d = designRef.current;
    if (!d) return;
    commitPage();
    const idx =
      which === "all"
        ? pagesRef.current.map((_, i) => i)
        : [pageIdxRef.current];
    const base = (d.name || "design").replace(/[^\w-]+/g, "_");
    setBusy("download");
    try {
      if (format === "pdf") {
        const { jsPDF } = await import("jspdf");
        const landscape = d.width > d.height;
        const pdf = new jsPDF({
          orientation: landscape ? "l" : "p",
          unit: "px",
          format: [d.width, d.height],
          hotfixes: ["px_scaling"],
          compress: true,
        });
        for (let n = 0; n < idx.length; n++) {
          // eslint-disable-next-line no-await-in-loop
          const url = await pageImage(idx[n], {
            format: "jpeg",
            multiplier: scale,
          });
          if (n > 0) pdf.addPage([d.width, d.height], landscape ? "l" : "p");
          pdf.addImage(url, "JPEG", 0, 0, d.width, d.height, undefined, "FAST");
        }
        pdf.save(`${base}.pdf`);
      } else {
        for (const i of idx) {
          // eslint-disable-next-line no-await-in-loop
          const url = await pageImage(i, {
            format: format === "jpg" ? "jpeg" : "png",
            multiplier: scale,
            transparent: format === "png-clear",
          });
          const a = document.createElement("a");
          a.href = url;
          a.download = `${base}${idx.length > 1 ? `-${i + 1}` : ""}.${format === "jpg" ? "jpg" : "png"}`;
          a.click();
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 350));
        }
      }
    } catch (e) {
      notify(errMessage(e) || "Download failed", "bad");
    } finally {
      setBusy(null);
    }
  }

  async function toGallery(mode: "NEW" | "UPDATE" | "ALL") {
    setMenu(null);
    const d = designRef.current;
    if (!d) return;
    setBusy("gallery");
    try {
      await save();
      if (mode === "ALL") {
        for (let i = 0; i < pagesRef.current.length; i++) {
          // eslint-disable-next-line no-await-in-loop
          const blob = await jpegUnder(i);
          // eslint-disable-next-line no-await-in-loop
          await dispatch(
            exportStudioDesign({
              id: d.id,
              blob,
              mode: "NEW",
              templateName: `${d.name} – page ${i + 1}`,
            }),
          ).unwrap();
        }
        notify(`${pagesRef.current.length} pages saved to your Gallery.`);
      } else {
        const blob = await jpegUnder(pageIdxRef.current);
        const item = (await dispatch(
          exportStudioDesign({ id: d.id, blob, mode, templateName: d.name }),
        ).unwrap()) as { id?: number | string } | undefined;
        setDesign((x) =>
          x ? { ...x, galleryId: item?.id ?? x.galleryId } : x,
        );
        notify(
          mode === "UPDATE"
            ? "Gallery image updated. Templates using it now show the new design."
            : "Saved to your Gallery.",
        );
      }
    } catch (e) {
      notify(errMessage(e), "bad");
    } finally {
      setBusy(null);
    }
  }

  async function toProfile() {
    setMenu(null);
    if (
      !window.confirm(
        "Use this page as your WhatsApp Business profile photo? Customers will see it right away.",
      )
    )
      return;
    setBusy("profile");
    try {
      await dispatch(
        setStudioProfilePicture(await jpegUnder(pageIdxRef.current)),
      ).unwrap();
      notify("Your WhatsApp profile photo is updated.");
    } catch (e) {
      notify(errMessage(e), "bad");
    } finally {
      setBusy(null);
    }
  }

  async function rename(name: string) {
    const clean = name.trim().slice(0, 120) || "Untitled design";
    setDesign((d) => (d ? { ...d, name: clean } : d));
    await save({ name: clean });
  }

  // ------------------------------------------------------------------ magic resize

  async function resizeDesign({
    inPlace,
    targets: list,
  }: {
    inPlace: boolean;
    targets: ResizeTarget[];
  }) {
    const d = designRef.current;
    if (!d) return [];
    commitPage();
    setBusy("resize");
    try {
      if (inPlace) {
        const t = list[0];
        const pages: PageDoc[] = [];
        for (const p of pagesRef.current) {
          // eslint-disable-next-line no-await-in-loop
          pages.push({
            ...p,
            json: await resizePageJson(
              p.json,
              d.width,
              d.height,
              t.width,
              t.height,
            ),
          });
        }
        pagesRef.current = pages;
        const next = {
          ...d,
          width: t.width,
          height: t.height,
          formatKey: t.key,
        };
        designRef.current = next;
        setDesign(next);
        thumbJsonRef.current = {};
        await loadPage(pageIdxRef.current);
        await save({ width: t.width, height: t.height, formatKey: t.key });
        pushHistory(true);
        history.current.stack = history.current.stack.slice(-1);
        history.current.index = 0;
        setCanUndo(false);
        setCanRedo(false);
        refreshThumbs();
        notify(`Resized to ${t.width} × ${t.height}.`);
        return [];
      }
      const created: (DesignDetail & { label: string })[] = [];
      for (const t of list) {
        const pages: PageDoc[] = [];
        for (const p of pagesRef.current) {
          // eslint-disable-next-line no-await-in-loop
          pages.push({
            id: newId(),
            json: await resizePageJson(
              p.json,
              d.width,
              d.height,
              t.width,
              t.height,
            ),
          });
        }
        // eslint-disable-next-line no-await-in-loop
        const thumb = await renderPageThumb(
          pages[0].json,
          t.width,
          t.height,
          360,
        );
        // eslint-disable-next-line no-await-in-loop
        const nd = (await dispatch(
          createStudioDesign({
            formatKey: t.key,
            width: t.width,
            height: t.height,
            name: `${d.name} – ${t.label}`,
            canvasJson: stringifyDoc(pages),
            thumbnailDataUrl: thumb,
          }),
        ).unwrap()) as DesignDetail;
        created.push({ ...nd, label: t.label });
      }
      notify(
        `${created.length} resized ${created.length === 1 ? "copy" : "copies"} created.`,
      );
      return created;
    } catch (e) {
      notify(errMessage(e), "bad");
      return [];
    } finally {
      setBusy(null);
    }
  }

  // ------------------------------------------------------------------ brand kit

  /** Saves the brand kit; fields left out keep their current value. */
  async function saveBrand(change: Partial<BrandKit>) {
    const next: BrandKit = { ...brand, ...change };
    try {
      const saved = (await dispatch(
        saveStudioBrandKit({
          colors: next.colors,
          headingFont: next.headingFont,
          bodyFont: next.bodyFont,
          logoGalleryIds: (next.logos || []).map((l) => l.galleryId),
        }),
      ).unwrap()) as BrandKit;
      setBrand(saved);
      await loadFonts([saved.headingFont, saved.bodyFont]);
      return saved;
    } catch (e) {
      notify(errMessage(e), "bad");
      return null;
    }
  }

  /** Brand fonts on this page: the biggest texts get the heading font, the rest the body font. */
  function applyBrandFonts() {
    const c = canvasRef.current;
    if (!c) return;
    const texts = objectsOf(c).filter((o) => kindOf(o) === "text");
    if (!texts.length || (!brand.headingFont && !brand.bodyFont)) return;
    const max = Math.max(...texts.map((t) => t.fontSize * (t.scaleY || 1)));
    texts.forEach((t) => {
      const heading = t.fontSize * (t.scaleY || 1) >= max * 0.6;
      const f = heading
        ? brand.headingFont || brand.bodyFont
        : brand.bodyFont || brand.headingFont;
      if (f) {
        t.set({ fontFamily: f });
        t.initDimensions?.();
      }
    });
    c.requestRenderAll();
    changed();
    notify("Brand fonts applied to this page.");
  }

  // ------------------------------------------------------------------ the editor API passed to panels and toolbars

  const c = canvasRef.current;
  const ed = {
    canvas: c,
    design,
    features,
    brand,
    selected,
    kind: kindOf(selected),
    busy,
    setBusy,
    notify,
    panel,
    setPanel,

    add,
    insertStockPhoto: useStockPhoto,
    insertImage: addImage,
    useStockPhoto,

    setBackgroundPhoto,
    imageToBackground,
    setBackground,
    removeBackgroundPhoto,

    setProp,
    each,
    removeSelected,
    duplicate,
    order,
    moveLayer,
    toggleLock,
    toggleVisible,
    align,
    distribute,
    group,
    ungroup,
    selectLayer,

    copyStyle,
    painting,

    startCrop,
    endCrop,
    crop,
    cropImg: cropRef.current?.img,

    removeImageBackground,
    upscaleImage,
    magicLayers,
    setDrawing,

    changed,
    refresh,

    saveBrand,
    applyBrandFonts,

    addPage,
    makeCombo,
    makeText,

    openVideo: onOpenVideo
      ? (start: VideoStart) => {
          save().finally(() => onOpenVideo(start));
        }
      : undefined,

    snapOn,
    setSnapOn: (v: boolean) => {
      snapOnRef.current = v;
      setSnapOn(v);
    },
  };

  // ------------------------------------------------------------------ render

  if (loadError) {
    return (
      <div className="fixed inset-0 z-[900] flex flex-col bg-st-stage items-center justify-center gap-3">
        <p className="text-st-red text-[13px] mt-2">{loadError}</p>
        <button
          className="inline-flex items-center justify-center min-h-[40px] px-3.5 rounded-[10px] border border-st-line bg-white text-st-ink font-semibold text-[14px]"
          onClick={onClose}
        >
          Back to Studio
        </button>
      </div>
    );
  }

  const square = !!design && design.width === design.height;
  const many = pageIds.length > 1;
  const isProfile = design?.formatKey === "PROFILE";

  return (
    <div
      className={`fixed inset-0 z-[900] flex flex-col bg-st-stage ${painting ? " cursor-copy" : ""}`}
    >
      {/* ---------------- top bar */}
      <header className="flex items-center justify-between gap-3 h-[58px] pl-2 pr-3 flex-none bg-[#0b1220] text-white max-[820px]:h-auto max-[820px]:flex-wrap max-[820px]:p-2">
        {/* Ambient glow */}
        <div className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full bg-brand-main/25 blur-3xl" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-white/10 via-white/[0.04] to-transparent" />
        <div className="flex items-center gap-2 min-w-0">
          <button
            className={iconBtnDark}
            onClick={close}
            aria-label="Back to Studio"
            title="Back to Studio"
          >
            <Icon name="back" />
          </button>
          {design && <NameField value={design.name} onSave={rename} />}
          <span
            className={`text-xs whitespace-nowrap max-[1100px]:hidden ${saveState === "error" ? "text-[#ffb4a3]" : "text-[#a9c5ba]"}`}
            role="status"
          >
            {saveState === "saved" && (
              <span className="inline-flex items-center gap-1">
                <Icon name="cloud" size={14} /> Saved
              </span>
            )}
            {saveState === "dirty" && "Unsaved changes"}
            {saveState === "saving" && "Saving…"}
            {saveState === "error" && (
              <button
                className="bg-transparent border-0 text-[#9fd9bd] font-semibold text-[13px] cursor-pointer px-1.5 py-1"
                onClick={() => save()}
              >
                Not saved. Retry
              </button>
            )}
          </span>
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <button
            className={iconBtnDark}
            disabled={!canUndo}
            onClick={() => restore(-1)}
            aria-label="Undo"
            title="Undo (Ctrl+Z)"
          >
            <Icon name="undo" />
          </button>
          <button
            className={iconBtnDark}
            disabled={!canRedo}
            onClick={() => restore(1)}
            aria-label="Redo"
            title="Redo (Ctrl+Y)"
          >
            <Icon name="redo" />
          </button>
          <span className="w-px h-6 bg-white/20 mx-1" />
          <button
            className={topBtnDark}
            disabled={!design}
            onClick={() => setDialog("resize")}
            title="Magic resize"
          >
            <Icon name="resize" size={16} /> Resize
          </button>
          <button
            className={iconBtnDark}
            disabled={!design}
            onClick={() => setDialog("versions")}
            aria-label="Version history"
            title="Version history"
          >
            <Icon name="history" />
          </button>
          <button
            className={iconBtnDark}
            onClick={() => setDialog("shortcuts")}
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts (?)"
          >
            <Icon name="keyboard" />
          </button>
        </div>

        <div className="flex items-center gap-2 min-w-0 max-[820px]:w-full max-[820px]:overflow-x-auto max-[820px]:pb-0.5">
          <div className="relative max-[820px]:static">
            <button
              className={topBtnDark}
              disabled={!design || busy === "download"}
              onClick={() => setMenu(menu === "download" ? null : "download")}
              aria-expanded={menu === "download"}
            >
              <Icon name="download" size={18} />{" "}
              {busy === "download" ? "Preparing…" : "Download"}
            </button>
            {menu === "download" && (
              <DownloadMenu pages={pageIds.length} onDownload={download} />
            )}
          </div>
          {square && (
            <button
              className={topBtnDark}
              disabled={!!busy}
              onClick={toProfile}
              title="POST /api/whatsapp/profile/picture"
            >
              <Icon name="profile" size={18} />{" "}
              {busy === "profile" ? "Updating…" : "Set as profile photo"}
            </button>
          )}
          <div className="relative max-[820px]:static">
            <button
              className={`bg-web-primary text-web-background flex ${topBtn} gap-1 rounded-md items-center disabled:opacity-50`}
              disabled={!!busy || !design}
              onClick={() =>
                design?.galleryId || many
                  ? setMenu(menu === "gallery" ? null : "gallery")
                  : toGallery("NEW")
              }
            >
              <Icon name="gallery" size={18} />{" "}
              {busy === "gallery" ? "Saving…" : "Save to Gallery"}
            </button>
            {menu === "gallery" && (
              <div className={menuBox} role="menu">
                {design?.galleryId && (
                  <button
                    className={menuItem}
                    role="menuitem"
                    onClick={() => toGallery("UPDATE")}
                  >
                    <strong className="block text-[14px]">
                      Update the Gallery image
                    </strong>
                    <span className="block text-xs text-st-muted mt-0.5">
                      Replaces your last export everywhere it is used
                    </span>
                  </button>
                )}
                <button
                  className={menuItem}
                  role="menuitem"
                  onClick={() => toGallery("NEW")}
                >
                  <strong className="block text-[14px]">
                    {many
                      ? `Save page ${pageIndex + 1} as a new image`
                      : "Save as a new image"}
                  </strong>
                  <span className="block text-xs text-st-muted mt-0.5">
                    Keeps earlier exports as they are
                  </span>
                </button>
                {many && (
                  <button
                    className={menuItem}
                    role="menuitem"
                    onClick={() => toGallery("ALL")}
                  >
                    <strong className="block text-[14px]">
                      Save all {pageIds.length} pages
                    </strong>
                    <span className="block text-xs text-st-muted mt-0.5">
                      One Gallery image per page (for carousels)
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-[76px_312px_minmax(0,1fr)] min-[821px]:max-[1100px]:grid-cols-[68px_270px_minmax(0,1fr)] max-[820px]:grid-cols-1 max-[820px]:grid-rows-[auto_auto_minmax(0,1fr)]">
        {/* ---------------- rail */}
        <nav
          className="bg-st-night relative flex flex-col gap-0.5 px-1.5 py-2 overflow-y-auto max-[820px]:flex-row max-[820px]:overflow-x-auto max-[820px]:p-1"
          aria-label="Design tools"
        >
          <div className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full bg-brand-main/25 blur-3xl" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-white/10 via-white/[0.04] to-transparent" />

          {RAIL.map(([key, label, icon]) => (
            <button
              key={key}
              className={`${railBtn} ${panel === key ? railOn : railOff}`}
              aria-pressed={panel === key}
              onClick={() => setPanel(key)}
            >
              <Icon name={icon} size={22} />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        {/* ---------------- side panel */}
        <aside
          className="bg-white border-r border-st-line overflow-y-auto max-[820px]:max-h-[34vh] max-[820px]:border-r-0 max-[820px]:border-b"
          aria-label={`${panel} panel`}
        >
          {design && c && <Panels ed={ed} />}
        </aside>

        {/* ---------------- canvas */}
        <main className="flex flex-col min-w-0 min-h-0 relative">
          <ContextToolbar ed={ed} />

          <div
            className="flex-1 min-h-0 overflow-auto grid place-items-center p-8"
            ref={stageRef}
            onMouseDown={(e) => {
              if (e.target === stageRef.current) {
                endCrop();
                c?.discardActiveObject();
                c?.requestRenderAll();
                setSelected(null);
              }
            }}
          >
            {!design && (
              <p className="m-0 text-st-muted">Opening your design…</p>
            )}
            <div
              className={`shadow-[0_1px_3px_rgba(16,35,30,0.12),0_18px_40px_-20px_rgba(16,35,30,0.45)] leading-[0] relative${isProfile ? " after:content-[''] after:absolute after:inset-0 after:rounded-full after:border-2 after:border-dashed after:border-white/90 after:shadow-[0_0_0_1px_rgba(16,35,30,0.35)] after:pointer-events-none after:z-[2]" : ""}`}
              style={{ display: design ? "block" : "none" }}
              title={
                isProfile
                  ? "WhatsApp shows profile photos as a circle. Keep the important part inside the dashed line."
                  : undefined
              }
            >
              <canvas ref={canvasElRef} />
            </div>
          </div>

          <footer className="flex-none flex items-center justify-between gap-3 px-3 py-2 bg-white border-t border-st-line">
            <PagesBar
              ids={pageIds}
              index={pageIndex}
              thumbs={thumbs}
              ratio={design ? design.width / design.height : 1}
              onGo={goToPage}
              onAdd={() => addPage()}
              onDuplicate={duplicatePage}
              onDelete={deletePage}
              onMove={movePage}
            />
            <div className="flex items-center gap-0.5 p-1 rounded-xl bg-white shadow-[0_6px_18px_-8px_rgba(0,0,0,0.3),0_0_0_1px_rgba(16,35,30,0.06)]">
              <button
                className={iconBtnSm}
                onClick={() => setZoomFactor((z) => Math.max(0.2, z / 1.2))}
                aria-label="Zoom out"
              >
                <Icon name="minus" size={16} />
              </button>
              <button
                className="min-w-[54px] border-0 bg-transparent text-[13px] font-semibold cursor-pointer tabular-nums text-st-ink"
                onClick={() => setZoomFactor(1)}
                title="Fit to screen"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                className={iconBtnSm}
                onClick={() => setZoomFactor((z) => Math.min(5, z * 1.2))}
                aria-label="Zoom in"
              >
                <Icon name="plus" size={16} />
              </button>
            </div>
          </footer>
        </main>
      </div>

      {dialog === "resize" && design && (
        <ResizeDialog
          design={design}
          busy={busy === "resize"}
          onClose={() => setDialog(null)}
          onResize={resizeDesign}
          onOpen={(id: number | string) => {
            setDialog(null);
            save().then(() => onOpenDesign?.(id));
          }}
        />
      )}
      {dialog === "versions" && design && (
        <VersionsDrawer
          design={design}
          onClose={() => setDialog(null)}
          notify={notify}
          beforeSave={async () => {
            await save();
            return renderPageThumb(
              pagesRef.current[0].json,
              design.width,
              design.height,
              360,
            );
          }}
          onRestored={async (d: DesignDetail) => {
            await reloadFrom(d);
            setDialog(null);
            notify(
              "Version restored. The design before it is kept in the history.",
            );
          }}
        />
      )}
      {dialog === "shortcuts" && (
        <ShortcutsDialog onClose={() => setDialog(null)} />
      )}

      {toast && (
        <div
          className={`fixed left-1/2 bottom-7 -translate-x-1/2 z-[1000] max-w-[min(560px,calc(100vw-32px))] px-[18px] py-3 rounded-xl text-[14px] font-medium text-white shadow-[0_16px_40px_-12px_rgba(0,0,0,0.45)] animate-st-pop-in ${
            toast.kind === "good"
              ? "bg-[#0b5e46]"
              : toast.kind === "bad"
                ? "bg-[#8f2c1d]"
                : "bg-st-night"
          }`}
          role={toast.kind === "bad" ? "alert" : "status"}
        >
          {toast.text}
        </div>
      )}
      {menu && (
        <div className="fixed inset-0 z-20" onClick={() => setMenu(null)} />
      )}
    </div>
  );
}
